import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Plus, RefreshCw, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AdminLayout } from "./AdminLayout";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Textarea } from "../ui/textarea";
import { cloneFormationsPageData, createEmptyPriceLine, createEmptyProgramSection, createEmptyQcmAnswer, createEmptyQcmQuestion, createEmptyQcmTest, normalizeFormationsPageData } from "../../lib/formationsDefaults";
import { getApiBase } from "../../lib/apiBase";
import { AdminImageUploadField } from "./formations/AdminImageUploadField";
import { AdminQcmQuestionEditor } from "./formations/AdminQcmQuestionEditor";

const sortByOrder = (items = []) => [...items].sort((left, right) => left.order - right.order || left.title.localeCompare(right.title));

export default function AdminFormationsPage() {
  const [pageData, setPageData] = useState(() => cloneFormationsPageData());
  const [selectedFormationId, setSelectedFormationId] = useState("revit-toutes-les-bases");
  const [selectedQcmId, setSelectedQcmId] = useState("qcm-revit-toutes-les-bases");
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingFormationId, setUploadingFormationId] = useState("");
  const [uploadingQuestionId, setUploadingQuestionId] = useState("");

  const fetchPageData = async () => {
    try {
      const API = getApiBase();
      const { data } = await axios.get(`${API}/api/admin/formations-page`, { withCredentials: true });
      const normalized = normalizeFormationsPageData(data);
      setPageData(normalized);
      setSelectedFormationId((current) => (normalized.formations.some((item) => item.id === current) ? current : normalized.formations[0]?.id || current));
      setSelectedQcmId((current) => (normalized.qcms.some((item) => item.id === current) ? current : normalized.qcms[0]?.id || current));
    } catch {
      const fallback = cloneFormationsPageData();
      setPageData(fallback);
      toast.error("Impossible de charger la page Formations. Les valeurs par défaut restent disponibles.");
    }
  };

  useEffect(() => {
    fetchPageData();
  }, []);

  const sortedFormations = useMemo(() => sortByOrder(pageData.formations), [pageData.formations]);
  const sortedQcms = useMemo(() => sortByOrder(pageData.qcms), [pageData.qcms]);
  const selectedFormation = sortedFormations.find((item) => item.id === selectedFormationId) || sortedFormations[0];
  const selectedQcm = sortedQcms.find((item) => item.id === selectedQcmId) || sortedQcms[0];

  const updateFormation = (formationId, updater) => {
    setPageData((current) => ({
      ...current,
      formations: current.formations.map((formation) => (formation.id === formationId ? updater(formation) : formation)),
    }));
  };

  const updateQcm = (qcmId, updater) => {
    setPageData((current) => ({
      ...current,
      qcms: current.qcms.map((qcm) => (qcm.id === qcmId ? updater(qcm) : qcm)),
    }));
  };

  const uploadImage = async (file) => {
    const API = getApiBase();
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await axios.post(`${API}/api/admin/formations/media`, formData, { withCredentials: true });
    return data;
  };

  const handleFormationImageUpload = async (formationId, file) => {
    try {
      setUploadingFormationId(formationId);
      const media = await uploadImage(file);
      updateFormation(formationId, (formation) => ({ ...formation, imageUrl: media.url }));
      toast.success("Image de formation importée.");
    } catch {
      toast.error("L’upload de l’image de formation a échoué.");
    } finally {
      setUploadingFormationId("");
    }
  };

  const handleQuestionImageUpload = async (qcmId, questionId, file) => {
    try {
      setUploadingQuestionId(questionId);
      const media = await uploadImage(file);
      updateQcm(qcmId, (qcm) => ({ ...qcm, questions: qcm.questions.map((question) => (question.id === questionId ? { ...question, imageUrl: media.url } : question)) }));
      toast.success("Image de question importée.");
    } catch {
      toast.error("L’upload de l’image de question a échoué.");
    } finally {
      setUploadingQuestionId("");
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const API = getApiBase();
      const payload = normalizeFormationsPageData(pageData);
      const { data } = await axios.put(`${API}/api/admin/formations-page`, payload, { withCredentials: true });
      setPageData(normalizeFormationsPageData(data));
      toast.success("La page Formations a été sauvegardée.");
    } catch {
      toast.error("La sauvegarde a échoué. Vérifiez vos modifications puis réessayez.");
    } finally {
      setIsSaving(false);
    }
  };

  const addQcm = () => {
    const nextQcm = createEmptyQcmTest();
    setPageData((current) => ({ ...current, qcms: [...current.qcms, nextQcm] }));
    setSelectedQcmId(nextQcm.id);
  };

  const addQuestion = (qcmId) => {
    const nextQuestion = createEmptyQcmQuestion(qcmId);
    updateQcm(qcmId, (qcm) => ({ ...qcm, questions: [...qcm.questions, nextQuestion] }));
  };

  const updateQuestion = (qcmId, questionId, updates) => {
    updateQcm(qcmId, (qcm) => ({ ...qcm, questions: qcm.questions.map((question) => (question.id === questionId ? { ...question, ...updates } : question)) }));
  };

  const removeQuestion = (qcmId, questionId) => {
    updateQcm(qcmId, (qcm) => ({ ...qcm, questions: qcm.questions.filter((question) => question.id !== questionId) }));
  };

  const addAnswer = (qcmId, questionId) => {
    updateQcm(qcmId, (qcm) => ({
      ...qcm,
      questions: qcm.questions.map((question) => (question.id === questionId ? { ...question, answers: [...question.answers, createEmptyQcmAnswer(question.id)] } : question)),
    }));
  };

  const updateAnswer = (qcmId, questionId, answerId, text) => {
    updateQcm(qcmId, (qcm) => ({
      ...qcm,
      questions: qcm.questions.map((question) => (question.id === questionId ? { ...question, answers: question.answers.map((answer) => (answer.id === answerId ? { ...answer, text } : answer)) } : question)),
    }));
  };

  const removeAnswer = (qcmId, questionId, answerId) => {
    updateQcm(qcmId, (qcm) => ({
      ...qcm,
      questions: qcm.questions.map((question) => {
        if (question.id !== questionId) return question;
        const answers = question.answers.filter((answer) => answer.id !== answerId);
        return { ...question, answers, correctAnswerId: answers.some((answer) => answer.id === question.correctAnswerId) ? question.correctAnswerId : answers[0]?.id || "" };
      }),
    }));
  };

  return (
    <AdminLayout description="Gérez ici les cartes de formations, les tests QCM, le parcours recommandé Revit et le bloc contact de la page publique /formations." title="Formations">
      <Card className="rounded-[2rem] border border-[#d9e7d5]/90 bg-white/92 shadow-[0_24px_70px_-48px_rgba(15,118,110,0.2)]" data-testid="admin-formations-actions-card">
        <CardContent className="flex flex-col gap-4 p-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]" data-testid="admin-formations-actions-eyebrow">Page publique</div>
            <p className="mt-2 max-w-3xl text-sm leading-7 text-slate-600" data-testid="admin-formations-actions-description">
              Tous les changements ci-dessous alimentent directement la page publique <span className="font-semibold text-slate-900">/formations</span>.
            </p>
          </div>
          <div className="flex flex-wrap gap-3" data-testid="admin-formations-actions-buttons">
            <Button className="rounded-full border-[#d8ece4] bg-white/90 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid="admin-formations-reload-button" onClick={fetchPageData} type="button" variant="outline">
              <RefreshCw className="mr-2 h-4 w-4" />
              Recharger
            </Button>
            <Button className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]" data-testid="admin-formations-save-button" disabled={isSaving} onClick={handleSave} type="button">
              <Save className="mr-2 h-4 w-4" />
              {isSaving ? "Enregistrement..." : "Enregistrer les changements"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs className="space-y-6" data-testid="admin-formations-tabs" defaultValue="formations">
        <TabsList className="grid w-full grid-cols-2 gap-2 rounded-[1.4rem] bg-white/88 p-2 lg:grid-cols-4" data-testid="admin-formations-tabs-list">
          <TabsTrigger data-testid="admin-formations-tab-formations" value="formations">Formations</TabsTrigger>
          <TabsTrigger data-testid="admin-formations-tab-qcms" value="qcms">Tests QCM</TabsTrigger>
          <TabsTrigger data-testid="admin-formations-tab-learning-path" value="learning-path">Parcours Revit</TabsTrigger>
          <TabsTrigger data-testid="admin-formations-tab-contact" value="contact">Bloc contact</TabsTrigger>
        </TabsList>

        <TabsContent value="formations">
          <div className="grid gap-6 xl:grid-cols-[24rem_1fr]" data-testid="admin-formations-tab-content-formations">
            <Card className="rounded-[2rem] border border-[#d9e7d5]/90 bg-white/92 shadow-[0_24px_70px_-48px_rgba(15,118,110,0.2)]" data-testid="admin-formations-list-card">
              <CardContent className="p-6">
                <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]" data-testid="admin-formations-list-eyebrow">Cartes formations</div>
                <div className="mt-5 grid gap-3" data-testid="admin-formations-list">
                  {sortedFormations.map((formation) => (
                    <button
                      className={`rounded-[1.4rem] border px-4 py-4 text-left transition ${formation.id === selectedFormationId ? "border-[#0f766e] bg-[#ecfdf5] shadow-[0_20px_50px_-40px_rgba(15,118,110,0.35)]" : "border-[#d9e7d5] bg-white hover:border-[#99f6e4] hover:bg-[#f8fcf3]"}`}
                      data-testid={`admin-formations-select-${formation.id}`}
                      key={formation.id}
                      onClick={() => setSelectedFormationId(formation.id)}
                      type="button"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0f766e]">Ordre {formation.order}</div>
                          <div className="mt-2 text-sm font-semibold text-slate-950">{formation.title}</div>
                        </div>
                        <div className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${formation.isActive ? "bg-[#dcfce7] text-[#166534]" : "bg-slate-100 text-slate-500"}`} data-testid={`admin-formations-status-${formation.id}`}>
                          {formation.isActive ? "Actif" : "Inactif"}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {selectedFormation ? (
              <Card className="rounded-[2rem] border border-[#d9e7d5]/90 bg-white/92 shadow-[0_24px_70px_-48px_rgba(15,118,110,0.2)]" data-testid="admin-formations-editor-card">
                <CardContent className="p-6 sm:p-8">
                  <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]" data-testid="admin-formations-editor-eyebrow">Formation sélectionnée</div>
                      <h2 className="bim-display mt-3 text-3xl font-semibold tracking-tight text-slate-950" data-testid="admin-formations-editor-title">{selectedFormation.title}</h2>
                    </div>
                    <div className="flex items-center gap-3 rounded-full border border-[#d8ece4] bg-[#f8fcf3] px-4 py-2">
                      <Switch checked={selectedFormation.isActive} data-testid="admin-formations-active-switch" onCheckedChange={(value) => updateFormation(selectedFormation.id, (formation) => ({ ...formation, isActive: value }))} />
                      <span className="text-sm font-medium text-slate-700">Visible sur le site</span>
                    </div>
                  </div>

                  <div className="mt-8 grid gap-6 lg:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="admin-formations-title">Titre</Label>
                      <Input data-testid="admin-formations-title-input" id="admin-formations-title" onChange={(event) => updateFormation(selectedFormation.id, (formation) => ({ ...formation, title: event.target.value }))} value={selectedFormation.title} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="admin-formations-order">Ordre d’affichage</Label>
                      <Input data-testid="admin-formations-order-input" id="admin-formations-order" min="0" onChange={(event) => updateFormation(selectedFormation.id, (formation) => ({ ...formation, order: Number(event.target.value) || 0 }))} type="number" value={selectedFormation.order} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="admin-formations-days">Durée (jours)</Label>
                      <Input data-testid="admin-formations-days-input" id="admin-formations-days" min="0" onChange={(event) => updateFormation(selectedFormation.id, (formation) => ({ ...formation, durationDays: Number(event.target.value) || 0 }))} type="number" value={selectedFormation.durationDays} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="admin-formations-hours">Durée (heures)</Label>
                      <Input data-testid="admin-formations-hours-input" id="admin-formations-hours" min="0" onChange={(event) => updateFormation(selectedFormation.id, (formation) => ({ ...formation, durationHours: Number(event.target.value) || 0 }))} type="number" value={selectedFormation.durationHours} />
                    </div>
                  </div>

                  <div className="mt-6">
                    <AdminImageUploadField
                      fallbackText="Aucune image définie pour cette formation."
                      imageUrl={selectedFormation.imageUrl}
                      inputId="admin-formations-image-url"
                      inputTestId="admin-formations-image-url-input"
                      isUploading={uploadingFormationId === selectedFormation.id}
                      label="Photo de la formation (URL ou upload)"
                      onChange={(value) => updateFormation(selectedFormation.id, (formation) => ({ ...formation, imageUrl: value }))}
                      onFileSelected={(file) => handleFormationImageUpload(selectedFormation.id, file)}
                      previewAlt={selectedFormation.title}
                      previewImageTestId="admin-formations-image-preview"
                      previewTestId="admin-formations-image-preview-wrapper"
                      uploadButtonTestId="admin-formations-image-upload-button"
                      wrapperTestId="admin-formations-image-field"
                    />
                  </div>

                  <div className="mt-6 grid gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="admin-formations-description">Description</Label>
                      <Textarea className="min-h-[140px]" data-testid="admin-formations-description-input" id="admin-formations-description" onChange={(event) => updateFormation(selectedFormation.id, (formation) => ({ ...formation, description: event.target.value }))} value={selectedFormation.description} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="admin-formations-objectives">Objectifs</Label>
                      <Textarea className="min-h-[140px]" data-testid="admin-formations-objectives-input" id="admin-formations-objectives" onChange={(event) => updateFormation(selectedFormation.id, (formation) => ({ ...formation, objectives: event.target.value }))} value={selectedFormation.objectives} />
                    </div>
                  </div>

                  <div className="mt-10 rounded-[1.8rem] border border-[#d8ece4] bg-[#f8fcf3] p-5" data-testid="admin-formations-prices-block">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]">Prix HT</div>
                        <p className="mt-2 text-sm text-slate-600">Le prix principal affiché côté public sera automatiquement le tarif le plus élevé de cette liste.</p>
                      </div>
                      <Button className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]" data-testid="admin-formations-add-price-button" onClick={() => updateFormation(selectedFormation.id, (formation) => ({ ...formation, prices: [...formation.prices, createEmptyPriceLine(formation.id)] }))} type="button">
                        <Plus className="mr-2 h-4 w-4" />
                        Ajouter un tarif
                      </Button>
                    </div>
                    <div className="mt-5 space-y-4" data-testid="admin-formations-prices-list">
                      {selectedFormation.prices.map((price) => (
                        <div className="grid gap-3 rounded-[1.4rem] border border-white bg-white p-4 shadow-[0_16px_36px_-28px_rgba(15,118,110,0.22)] md:grid-cols-[1fr_12rem_auto]" data-testid={`admin-formations-price-row-${price.id}`} key={price.id}>
                          <Input data-testid={`admin-formations-price-label-input-${price.id}`} onChange={(event) => updateFormation(selectedFormation.id, (formation) => ({ ...formation, prices: formation.prices.map((entry) => (entry.id === price.id ? { ...entry, label: event.target.value } : entry)) }))} value={price.label} />
                          <Input data-testid={`admin-formations-price-amount-input-${price.id}`} min="0" onChange={(event) => updateFormation(selectedFormation.id, (formation) => ({ ...formation, prices: formation.prices.map((entry) => (entry.id === price.id ? { ...entry, amount: Number(event.target.value) || 0 } : entry)) }))} type="number" value={price.amount} />
                          <Button className="rounded-full border-slate-200 text-slate-600 hover:text-slate-900 disabled:opacity-50" data-testid={`admin-formations-remove-price-button-${price.id}`} disabled={selectedFormation.prices.length <= 1} onClick={() => updateFormation(selectedFormation.id, (formation) => ({ ...formation, prices: formation.prices.filter((entry) => entry.id !== price.id) }))} type="button" variant="outline">
                            <span aria-hidden="true">×</span>
                            <span className="sr-only">Supprimer le tarif</span>
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-10 rounded-[1.8rem] border border-[#d9e7d5] bg-white p-5 shadow-[0_20px_50px_-42px_rgba(15,118,110,0.18)]" data-testid="admin-formations-program-block">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]">Programme</div>
                        <p className="mt-2 text-sm text-slate-600">Chaque section contient un titre et une liste de sous-thèmes, un par ligne.</p>
                      </div>
                      <Button className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]" data-testid="admin-formations-add-program-section-button" onClick={() => updateFormation(selectedFormation.id, (formation) => ({ ...formation, programSections: [...formation.programSections, createEmptyProgramSection(formation.id)] }))} type="button">
                        <Plus className="mr-2 h-4 w-4" />
                        Ajouter une section
                      </Button>
                    </div>

                    <div className="mt-5 space-y-5" data-testid="admin-formations-program-sections-list">
                      {selectedFormation.programSections.map((section) => (
                        <div className="rounded-[1.6rem] border border-[#d8ece4] bg-[#f8fcf3] p-5" data-testid={`admin-formations-program-section-${section.id}`} key={section.id}>
                          <div className="flex justify-end">
                            <Button className="rounded-full border-slate-200 text-slate-600 hover:text-slate-900 disabled:opacity-50" data-testid={`admin-formations-remove-program-section-button-${section.id}`} disabled={selectedFormation.programSections.length <= 1} onClick={() => updateFormation(selectedFormation.id, (formation) => ({ ...formation, programSections: formation.programSections.filter((entry) => entry.id !== section.id) }))} type="button" variant="outline">
                              <Trash2 className="mr-2 h-4 w-4" />
                              Supprimer
                            </Button>
                          </div>
                          <div className="mt-4 grid gap-4">
                            <div className="space-y-2">
                              <Label>Titre de section</Label>
                              <Input data-testid={`admin-formations-program-section-title-input-${section.id}`} onChange={(event) => updateFormation(selectedFormation.id, (formation) => ({ ...formation, programSections: formation.programSections.map((entry) => (entry.id === section.id ? { ...entry, title: event.target.value } : entry)) }))} value={section.title} />
                            </div>
                            <div className="space-y-2">
                              <Label>Sous-thèmes (une ligne par élément)</Label>
                              <Textarea className="min-h-[120px]" data-testid={`admin-formations-program-section-items-input-${section.id}`} onChange={(event) => updateFormation(selectedFormation.id, (formation) => ({
                                ...formation,
                                programSections: formation.programSections.map((entry) => (
                                  entry.id === section.id
                                    ? { ...entry, items: event.target.value.split("\n").map((line) => line.trim()).filter(Boolean) }
                                    : entry
                                )),
                              }))} value={section.items.join("\n")} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : null}
          </div>
        </TabsContent>

        <TabsContent value="qcms">
          <div className="grid gap-6 xl:grid-cols-[24rem_1fr]" data-testid="admin-formations-tab-content-qcms">
            <Card className="rounded-[2rem] border border-[#d9e7d5]/90 bg-white/92 shadow-[0_24px_70px_-48px_rgba(15,118,110,0.2)]" data-testid="admin-qcms-list-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]" data-testid="admin-qcms-list-eyebrow">Tests QCM</div>
                    <p className="mt-2 text-sm text-slate-600">Ajoutez et éditez le contenu réel de chaque test.</p>
                  </div>
                  <Button className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]" data-testid="admin-qcms-add-test-button" onClick={addQcm} type="button">
                    <Plus className="mr-2 h-4 w-4" />
                    Nouveau test
                  </Button>
                </div>
                <div className="mt-5 grid gap-3" data-testid="admin-qcms-list">
                  {sortedQcms.map((qcm) => (
                    <button
                      className={`rounded-[1.4rem] border px-4 py-4 text-left transition ${qcm.id === selectedQcmId ? "border-[#0f766e] bg-[#ecfdf5] shadow-[0_20px_50px_-40px_rgba(15,118,110,0.35)]" : "border-[#d9e7d5] bg-white hover:border-[#99f6e4] hover:bg-[#f8fcf3]"}`}
                      data-testid={`admin-qcms-select-${qcm.id}`}
                      key={qcm.id}
                      onClick={() => setSelectedQcmId(qcm.id)}
                      type="button"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0f766e]">Ordre {qcm.order}</div>
                          <div className="mt-2 text-sm font-semibold text-slate-950">{qcm.title}</div>
                          <div className="mt-2 text-xs text-slate-500">{qcm.questions.length} question(s)</div>
                        </div>
                        <div className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${qcm.isActive ? "bg-[#dcfce7] text-[#166534]" : "bg-slate-100 text-slate-500"}`} data-testid={`admin-qcms-status-${qcm.id}`}>
                          {qcm.isActive ? "Actif" : "Inactif"}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {selectedQcm ? (
              <Card className="rounded-[2rem] border border-[#d9e7d5]/90 bg-white/92 shadow-[0_24px_70px_-48px_rgba(15,118,110,0.2)]" data-testid="admin-qcms-editor-card">
                <CardContent className="p-6 sm:p-8">
                  <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]" data-testid="admin-qcms-editor-eyebrow">Test sélectionné</div>
                      <h2 className="bim-display mt-3 text-3xl font-semibold tracking-tight text-slate-950" data-testid="admin-qcms-editor-title">{selectedQcm.title}</h2>
                    </div>
                    <div className="flex items-center gap-3 rounded-full border border-[#d8ece4] bg-[#f8fcf3] px-4 py-2">
                      <Switch checked={selectedQcm.isActive} data-testid="admin-qcms-active-switch" onCheckedChange={(value) => updateQcm(selectedQcm.id, (qcm) => ({ ...qcm, isActive: value }))} />
                      <span className="text-sm font-medium text-slate-700">Visible sur le site</span>
                    </div>
                  </div>

                  <div className="mt-8 grid gap-6 lg:grid-cols-2">
                    <div className="space-y-2 lg:col-span-2">
                      <Label htmlFor="admin-qcms-title">Titre</Label>
                      <Input data-testid="admin-qcms-title-input" id="admin-qcms-title" onChange={(event) => updateQcm(selectedQcm.id, (qcm) => ({ ...qcm, title: event.target.value }))} value={selectedQcm.title} />
                    </div>
                    <div className="space-y-2 lg:col-span-2">
                      <Label htmlFor="admin-qcms-description">Description courte</Label>
                      <Textarea className="min-h-[120px]" data-testid="admin-qcms-description-input" id="admin-qcms-description" onChange={(event) => updateQcm(selectedQcm.id, (qcm) => ({ ...qcm, description: event.target.value }))} value={selectedQcm.description} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="admin-qcms-order">Ordre d’affichage</Label>
                      <Input data-testid="admin-qcms-order-input" id="admin-qcms-order" min="0" onChange={(event) => updateQcm(selectedQcm.id, (qcm) => ({ ...qcm, order: Number(event.target.value) || 0 }))} type="number" value={selectedQcm.order} />
                    </div>
                  </div>

                  <div className="mt-10 rounded-[1.8rem] border border-[#d8ece4] bg-[#f8fcf3] p-5" data-testid="admin-qcms-questions-block">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]">Questions du test</div>
                        <p className="mt-2 text-sm text-slate-600">Chaque question contient un énoncé, une image optionnelle, plusieurs réponses et une seule bonne réponse.</p>
                      </div>
                      <Button className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]" data-testid="admin-qcms-add-question-button" onClick={() => addQuestion(selectedQcm.id)} type="button">
                        <Plus className="mr-2 h-4 w-4" />
                        Ajouter une question
                      </Button>
                    </div>

                    <div className="mt-6 space-y-5" data-testid="admin-qcms-questions-list">
                      {selectedQcm.questions.length ? (
                        selectedQcm.questions.map((question, questionIndex) => (
                          <AdminQcmQuestionEditor
                            canRemoveQuestion
                            isUploading={uploadingQuestionId === question.id}
                            key={question.id}
                            onAddAnswer={(questionId) => addAnswer(selectedQcm.id, questionId)}
                            onRemoveAnswer={(questionId, answerId) => removeAnswer(selectedQcm.id, questionId, answerId)}
                            onRemoveQuestion={(questionId) => removeQuestion(selectedQcm.id, questionId)}
                            onSelectCorrectAnswer={(questionId, answerId) => updateQuestion(selectedQcm.id, questionId, { correctAnswerId: answerId })}
                            onUpdateAnswer={(questionId, answerId, text) => updateAnswer(selectedQcm.id, questionId, answerId, text)}
                            onUpdateQuestion={(questionId, updates) => updateQuestion(selectedQcm.id, questionId, updates)}
                            onUploadQuestionImage={(questionId, file) => handleQuestionImageUpload(selectedQcm.id, questionId, file)}
                            qcmId={selectedQcm.id}
                            question={question}
                            questionIndex={questionIndex}
                          />
                        ))
                      ) : (
                        <div className="rounded-[1.6rem] border border-dashed border-[#99f6e4] bg-white px-6 py-8 text-center text-sm leading-7 text-slate-500" data-testid="admin-qcms-empty-questions-state">
                          Aucune question n’est encore configurée pour ce test. Ajoutez-en une pour activer le parcours public complet.
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : null}
          </div>
        </TabsContent>

        <TabsContent value="learning-path">
          <Card className="rounded-[2rem] border border-[#d9e7d5]/90 bg-white/92 shadow-[0_24px_70px_-48px_rgba(15,118,110,0.2)]" data-testid="admin-learning-path-card">
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]" data-testid="admin-learning-path-eyebrow">Parcours recommandé Revit</div>
                  <h2 className="bim-display mt-3 text-3xl font-semibold tracking-tight text-slate-950" data-testid="admin-learning-path-title">Bloc d’introduction visuel</h2>
                </div>
                <div className="flex items-center gap-3 rounded-full border border-[#d8ece4] bg-[#f8fcf3] px-4 py-2">
                  <Switch checked={pageData.learningPath.isActive} data-testid="admin-learning-path-active-switch" onCheckedChange={(value) => setPageData((current) => ({ ...current, learningPath: { ...current.learningPath, isActive: value } }))} />
                  <span className="text-sm font-medium text-slate-700">Visible sur le site</span>
                </div>
              </div>

              <div className="mt-8 grid gap-6 lg:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="admin-learning-path-heading">Titre</Label>
                  <Input data-testid="admin-learning-path-heading-input" id="admin-learning-path-heading" onChange={(event) => setPageData((current) => ({ ...current, learningPath: { ...current.learningPath, title: event.target.value } }))} value={pageData.learningPath.title} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin-learning-path-subtitle">Sous-texte</Label>
                  <Input data-testid="admin-learning-path-subtitle-input" id="admin-learning-path-subtitle" onChange={(event) => setPageData((current) => ({ ...current, learningPath: { ...current.learningPath, subtitle: event.target.value } }))} value={pageData.learningPath.subtitle} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contact">
          <Card className="rounded-[2rem] border border-[#d9e7d5]/90 bg-white/92 shadow-[0_24px_70px_-48px_rgba(15,118,110,0.2)]" data-testid="admin-contact-block-card">
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]" data-testid="admin-contact-block-eyebrow">Bloc contact</div>
                  <h2 className="bim-display mt-3 text-3xl font-semibold tracking-tight text-slate-950" data-testid="admin-contact-block-title">Appel à l’action final</h2>
                </div>
                <div className="flex items-center gap-3 rounded-full border border-[#d8ece4] bg-[#f8fcf3] px-4 py-2">
                  <Switch checked={pageData.contactBlock.isActive} data-testid="admin-contact-block-active-switch" onCheckedChange={(value) => setPageData((current) => ({ ...current, contactBlock: { ...current.contactBlock, isActive: value } }))} />
                  <span className="text-sm font-medium text-slate-700">Visible sur le site</span>
                </div>
              </div>

              <div className="mt-8 grid gap-6 lg:grid-cols-2">
                <div className="space-y-2 lg:col-span-2">
                  <Label htmlFor="admin-contact-block-heading">Titre</Label>
                  <Input data-testid="admin-contact-block-title-input" id="admin-contact-block-heading" onChange={(event) => setPageData((current) => ({ ...current, contactBlock: { ...current.contactBlock, title: event.target.value } }))} value={pageData.contactBlock.title} />
                </div>
                <div className="space-y-2 lg:col-span-2">
                  <Label htmlFor="admin-contact-block-description">Texte</Label>
                  <Textarea className="min-h-[140px]" data-testid="admin-contact-block-description-input" id="admin-contact-block-description" onChange={(event) => setPageData((current) => ({ ...current, contactBlock: { ...current.contactBlock, description: event.target.value } }))} value={pageData.contactBlock.description} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin-contact-block-primary-label">Bouton principal</Label>
                  <Input data-testid="admin-contact-block-primary-label-input" id="admin-contact-block-primary-label" onChange={(event) => setPageData((current) => ({ ...current, contactBlock: { ...current.contactBlock, primaryLabel: event.target.value } }))} value={pageData.contactBlock.primaryLabel} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin-contact-block-primary-link">Lien principal</Label>
                  <Input data-testid="admin-contact-block-primary-link-input" id="admin-contact-block-primary-link" onChange={(event) => setPageData((current) => ({ ...current, contactBlock: { ...current.contactBlock, primaryHref: event.target.value } }))} value={pageData.contactBlock.primaryHref} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin-contact-block-secondary-label">Email affiché</Label>
                  <Input data-testid="admin-contact-block-secondary-label-input" id="admin-contact-block-secondary-label" onChange={(event) => setPageData((current) => ({ ...current, contactBlock: { ...current.contactBlock, secondaryLabel: event.target.value } }))} value={pageData.contactBlock.secondaryLabel} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin-contact-block-secondary-link">Lien email</Label>
                  <Input data-testid="admin-contact-block-secondary-link-input" id="admin-contact-block-secondary-link" onChange={(event) => setPageData((current) => ({ ...current, contactBlock: { ...current.contactBlock, secondaryHref: event.target.value } }))} value={pageData.contactBlock.secondaryHref} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}